import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { setOfflineStatusInDB } from "../helpers";
import { fnCompareArrayElements, fnDictSetIndex } from "../shared";
// import { callbackFunctions } from "./shuteCallbackRegistry";

const lMessageBank = 50
let messageIdCounter = 0;

function generateUniqueID() {
    return messageIdCounter++
}

export type WebSocketCallback = (message: WebSocketMessage) => void;

// interface CallbackRegistryItem {
//   depth: number;
//   callbacks: WebSocketCallback[];
// }
export interface CallbackRegistryItem {
  depth: number;
  callbackKeys: string[]; // Store keys instead of actual callbacks
}

interface ContextListener {
  page: string;
  event: string;
  contexts: string[];
}

export interface WebSocketMessage {
  key?: number;
  event: string;
  subEvent?:string;
  data: any; 
  timestamp?: number;  // Add a timestamp to your WebSocketMessage interface
}
export interface QueuedEventListener {
  page: string;
  watchevents: string[];
  action: ('add'|'remove');
  sessionid: string;
  psPageSubContext:string;
}

export interface ShuteState {
  status: string;
  connected: boolean;
  cachingForOffline: boolean;
  offline:boolean;
  version: string;
  sessionid: string;
  current_event_listeners: string[];
  messages: (WebSocketMessage | null)[]; 
  pending_event_listeners: string[];
  queued_event_listeners: QueuedEventListener[];
  eventtypes: string[];
  currentSlot: number; // Keep track of the current slot
  eventContexts: Record<string, Record<string, string[]>>; // page -> event -> contexts
  callbackRegistry: Record<string, CallbackRegistryItem>;
}


const initialState: ShuteState = {
  status: "DISCONNECTED",
  connected: false,
  offline: true,
  cachingForOffline: false,
  version: "",
  sessionid: "",
  current_event_listeners: [], 
  pending_event_listeners: [], 
  queued_event_listeners: [], 
  eventtypes: [], 
  messages: Array(lMessageBank).fill(null), 
  currentSlot: 0, // Start at the first slot
  eventContexts: {},
  callbackRegistry: {},
};

export const shuteSlice = createSlice({ 
  name: "shute",
  initialState,
  reducers: {
    setStatus: (state, action: PayloadAction<string>) => {
      state.status = action.payload;
    },
    setConnected: (state, action: PayloadAction<boolean>) => {
      state.connected = action.payload;
    },
    setOffline: (state, action: PayloadAction<boolean>) => {
      setOfflineStatusInDB(action.payload)
      state.offline = action.payload;
    },
    setCachingForOffline: (state, action: PayloadAction<boolean>) => {
      state.cachingForOffline = action.payload;
    },    
    setVersion: (state, action: PayloadAction<string>) => {
      state.version = action.payload;
    },
    setSessionID: (state, action: PayloadAction<string>) => {
      state.sessionid = action.payload;
    },
    setCurrentEventListeners: (state, action: PayloadAction<string[]>) => {
      state.current_event_listeners = action.payload;

    },
    addPendingEventListener: (state, action: PayloadAction<string[]>) => {
      const newListeners = action.payload.filter(
        event => !state.pending_event_listeners.includes(event)
      );
      state.pending_event_listeners = [...state.pending_event_listeners, ...newListeners];
    },
    removePendingEventListener: (state, action: PayloadAction<string>) => {
      state.pending_event_listeners = state.pending_event_listeners.filter(event => event !== action.payload);
    },
    clearPendingEventListeners: (state) => {
      state.pending_event_listeners = [];
    },
    addQueuedEventListener: (state, action: PayloadAction<QueuedEventListener>) => {
      // console.log(action.payload)
      state.queued_event_listeners.push(action.payload);
    },
    removeQueuedEventListener: (state, action: PayloadAction<QueuedEventListener>) => {
      // console.log('going to remove a listener',action.payload)
      // console.log(state.queued_event_listeners)
      state.queued_event_listeners = state.queued_event_listeners.filter(
        listener => !fnCompareArrayElements(listener, action.payload)
      );
    },
    clearQueuedEventListeners: (state) => {
      state.queued_event_listeners = [];
    },
    
    addMessage: (state, action: PayloadAction<WebSocketMessage>) => {
      // Add the new message to the current slot
      const sEventName = action.payload.event
      // console.log(`slice got ${sEventName}`,action.payload.data)
      let tAddEvent:WebSocketMessage = {
        key:generateUniqueID(),
        event:sEventName,
        data: action.payload.data,
        timestamp: action.payload.timestamp || Date.now(),
      };
      const splitEvent = sEventName.split('.',3);
      if (splitEvent.length > 2) {
        tAddEvent.event = splitEvent[0] + '.' + splitEvent[1];
        tAddEvent.subEvent = splitEvent[2];
      } 
      state.messages[state.currentSlot] = tAddEvent
      if (!state.eventtypes.includes(tAddEvent.event)) {
        state.eventtypes.push(tAddEvent.event)
      }
      // Increment the current slot, resetting to 0 if it reaches 50
      state.currentSlot = (state.currentSlot + 1) % lMessageBank;
    },
    setListenerContext: (state, action: PayloadAction<{eventAction:('add'|'remove'),page:string,event:string,context:string}>) => {
      const { eventAction, page, event, context } = action.payload;
      if (eventAction=='add'){
        if (!state.eventContexts[page]) {
          state.eventContexts[page] = {};
        }
        if (!state.eventContexts?.[page]?.[event]) {
          // fnDictSetIndex(state.eventContexts,`${page}.${event}`,[context])
          state.eventContexts[page][event]=[context]

        } else if (!state.eventContexts[page][event].includes(context)) {
          state.eventContexts[page][event].push(context);
        }
      }
      else {
        state.eventContexts[page][event] = state.eventContexts[page][event].filter(ctx => ctx !== context);
        // Optionally remove the event if no contexts are listening to it
        if (state.eventContexts[page][event].length === 0) {
          delete state.eventContexts[page][event];
        }
    
        if (Object.keys(state.eventContexts[page]).length === 0) {
          delete state.eventContexts[page];
        }
      }
    },
    registerCallback: (state, action: PayloadAction<{ eventName: string; callbackName: string }>) => {
      const { eventName, callbackName } = action.payload;
      const depth = eventName.split('.').length;
      // console.log(`registering a callback ${eventName} ${callbackName}`)
      if (!state.callbackRegistry[eventName]) {
        state.callbackRegistry[eventName] = {
          depth,
          callbackKeys: [],
        };
      }    
      if (!state.callbackRegistry[eventName].callbackKeys.includes(callbackName)) {
        state.callbackRegistry[eventName].callbackKeys.push(callbackName);
      }
    },

    deregisterCallback: (state, action: PayloadAction<{ eventName: string; callbackName:string }>) => {
      const { eventName, callbackName } = action.payload;
      const registryItem = state.callbackRegistry[eventName];
      // console.log(`deregistering a callback ${eventName} ${callbackName}`)
      if (registryItem) {
        registryItem.callbackKeys = registryItem.callbackKeys.filter(key => key !== callbackName);

        if (registryItem.callbackKeys.length === 0) {
          delete state.callbackRegistry[eventName];
        }
      }
    },    
  }
});


export const { setStatus, setConnected, setVersion, setSessionID, setCurrentEventListeners, addMessage, setOffline, setCachingForOffline,addPendingEventListener
  ,clearQueuedEventListeners,clearPendingEventListeners,removePendingEventListener,removeQueuedEventListener,addQueuedEventListener,setListenerContext,registerCallback, deregisterCallback } = shuteSlice.actions;
export const selectStatus = (state: { shute: ShuteState }) => state.shute.status;
export const selectConnected = (state: { shute: ShuteState }) => state.shute.connected;
export const selectOffline = (state: { shute: ShuteState }) => state.shute.offline;
export const selectCachingForOffline = (state: { shute: ShuteState }) => state.shute.cachingForOffline;
export const selectVersion = (state: { shute: ShuteState }) => state.shute.version;
export const selectSessionID = (state: { shute: ShuteState }) => state.shute.sessionid;
export const selectIsEventNotified = (state: { shute: ShuteState }, eventName: string) => 
    state.shute.current_event_listeners?.includes(eventName) ?? false;
export const selectIsEventCallback = (state: { shute: ShuteState }, eventName: string, callbackName: string) => {
    const callbackItem = state.shute.callbackRegistry[eventName];
    return callbackItem ? callbackItem.callbackKeys.includes(callbackName) : false;
};
    
export const selectEventtypes = (state: { shute: ShuteState }) => state.shute.eventtypes;
export const selectCallbackRegistry = (state: { shute: ShuteState }) => state.shute.callbackRegistry;
export const selectPageEventContexts = (state: { shute: ShuteState }, page: string) => 
    state.shute.eventContexts[page]

export const selectEventContexts = (state: { shute: ShuteState }) => state.shute.eventContexts;
export const selectCurrentEventListeners = (state: { shute: ShuteState }) => state.shute.current_event_listeners;

export const selectPendingEventListeners = (state: { shute: ShuteState }) => state.shute.pending_event_listeners;
export const selectQueuedEventListeners = (state: { shute: ShuteState }) => state.shute.queued_event_listeners;
export const selectAndClearQueuedEventListeners = createSelector(
  (state: { shute: ShuteState }) => [...state.shute.queued_event_listeners], 
  (queuedEventListeners) => queuedEventListeners
);

export const selectCurrentEvent = (state: { shute: ShuteState }) => state.shute.messages[state.shute.currentSlot-1];
export const selectMessagesInOrder_old = (state: { shute: ShuteState }) => {
  const { messages, currentSlot } = state.shute;
  return [...messages.slice(currentSlot), ...messages.slice(0, currentSlot)];
};
export const selectMessagesInOrder = createSelector(
  (state: { shute: ShuteState }) => state.shute,
  (_, props) => props.reverseOrder, // this function extracts the reverseOrder prop
  (shute, reverseOrder) => {
    const { messages, currentSlot } = shute as ShuteState;
    const orderedMessages = [...messages.slice(currentSlot), ...messages.slice(0, currentSlot)].filter(message => message !== null);
    
    return reverseOrder ? orderedMessages.reverse() : orderedMessages;
  }
);


export const selectMessages = (state: { shute: ShuteState }) => state.shute.messages;
// export const selectCurrentEventOfType = (eventType: string) => (state: { shute: ShuteState }) => {
//   const currentMessage = state.shute.messages[state.shute.currentSlot];
//   if (currentMessage ? currentMessage.event === eventType : false) {
//     return currentMessage
//   } else {
//     return null
//   }
// };
export const selectIsEventListened = (
  state: { shute: ShuteState }, 
  page: string, 
  event: string, 
  lastPageSubContext: string
) => {
  const contextsListening = state.shute.eventContexts[page]?.[event];
  if (!contextsListening) {
    // No contexts are listening to this event
    return false;
  }

  return contextsListening.length > 0 && !contextsListening.includes(lastPageSubContext);
};


export default shuteSlice.reducer;